import { registerFont } from 'canvas'

const alphabet = 'АаБбВвГгДдЕеЁёЖжЗзИиЙйКкЛлМмНнОоПпРрСсТтУуФфХхЦцЧчШшЩщЪъЫыЬьЭэЮюЯя' +
  'AaBbCcDdEeFfGgHhIiJjKkLlMmNnOoPpQqRrSsTtUuVvWwXxYyZz' +
  '1234567890 .,/\\|"\':;<>?[]{}()-_=+~`!@#$%^&*№°⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻⁽⁾⁼₀₁₂₃₄₅₆₇₈₉₊₋₍₎₌«»'

export type Font = {
  italic: boolean
  width: number
  fontFamily: string
  usedFallback?: boolean
}

const isServer = typeof window === 'undefined'

// Cache loaded fonts to avoid reloading
const loadedFonts = new Set<string>()
const fallbackFonts = new Set<string>()
let loadingPromise: Promise<Font[]> | null = null

/**
 * Load Google Fonts universally (server + client)
 * @returns Is used fallback font
 */
export const loadFonts = async (editorFonts: Font[]): Promise<boolean> => {
  if (editorFonts.length === 0) {
    return Promise.resolve(false)
  }

  let usedFallbackFont = false

  // Filter out already loaded fonts
  const fontsToLoad = editorFonts.filter(font => {
    const key = `${font.fontFamily}-${font.width}-${font.italic}`
    return !loadedFonts.has(key)
  }).filter(font => {
    const key = `${font.fontFamily}-${font.width}-${font.italic}`
    if (fallbackFonts.has(key)) {
      useNuxtApp().$event('editor:used-browser-feature')
      usedFallbackFont = true
      return false
    }
    return true
  })

  if (fontsToLoad.length === 0) {
    return Promise.resolve(usedFallbackFont)
  }

  // Queue font loading
  if (loadingPromise) {
    await loadingPromise
  }

  loadingPromise = loadFontsInternal(fontsToLoad)
  
  try {
    const loadResult = await loadingPromise
    // Mark fonts as loaded
    loadResult.forEach(font => {
      const key = `${font.fontFamily}-${font.width}-${font.italic}`
      if (font.usedFallback) {
        fallbackFonts.add(key)
        useNuxtApp().$event('editor:used-browser-feature')
        usedFallbackFont = true
      } else {
        loadedFonts.add(key)
      }
    })
  } finally {
    loadingPromise = null
  }
  return Promise.resolve(usedFallbackFont)
}

const loadFontsInternal = async (editorFonts: Font[]): Promise<Font[]> => {
  // Build Google Fonts URL
  const { url, fallbackUrl } = buildGoogleFontsUrl(editorFonts)

  if (isServer) {
    await loadFontsServer(url, fallbackUrl)
    return editorFonts
  } else {
    return await loadFontsClient(url, fallbackUrl) ? editorFonts.map((font) => {
      return {
        ...font,
        usedFallback: true,
      }
    }) : editorFonts
  }
}

/**
 * Build Google Fonts URL with proper format
 */
const buildGoogleFontsUrl = (editorFonts: Font[]) => {
  // Group by family
  const familyMap = new Map<string, Set<string>>()
  
  for (const font of editorFonts) {
    if (!familyMap.has(font.fontFamily)) {
      familyMap.set(font.fontFamily, new Set())
    }
    
    // Format: 0,400 or 1,700 (italic flag, weight)
    const italic = font.italic ? '1' : '0'
    const weight = font.width.toString()
    familyMap.get(font.fontFamily)!.add(`${italic},${weight}`)
  }

  // Build URL parts
  const fonts: string[] = []
  const families: string[] = []
  
  for (const [ family, variants ] of familyMap.entries()) {
    const sortedVariants = Array.from(variants).sort().join(';')
    fonts.push(`family=${encodeURIComponent(family)}:ital,wght@${sortedVariants}`)
    families.push(`family=${encodeURIComponent(family)}`)
  }

  const url = `https://fonts.googleapis.com/css2?${fonts.join('&')}&text=${encodeURIComponent(alphabet)}&display=swap`
  const fallbackUrl =
    `https://fonts.googleapis.com/css2?${families.join('&')}&text=${encodeURIComponent(alphabet)}&display=swap`
  
  return { url, fallbackUrl }
}

/**
 * Server-side: Download font files and register with FabricJS
 */
const loadFontsServer = async (cssUrl: string, fallbackUrl: string): Promise<void> => {
  try {
    // Fetch CSS with proper user agent to get woff2 format
    const cssResponse = await new Promise<Response>((resolve, reject) => {
      fetch(cssUrl, {
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
        },
      }).then((response) => {
        resolve(response)
      }).catch(() => {
        fetch(fallbackUrl, {
          headers: {
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          },
        }).then((response) => {
          resolve(response)
        }).catch((e) => {
          reject(e ?? 'Failed to fetch all fonts')
        })
      })
    })
    const cssText = await cssResponse.text()

    // Extract font URLs from CSS
    const fontUrls = extractFontUrls(cssText)

    if (fontUrls.length === 0) {
      console.warn('No font URLs found in CSS')
      return
    }

    const fontPromises = fontUrls.map(async ({ url, family, weight, italic }) => {
      try {
        const fontResponse = await fetch(url)
        const fontBuffer = Buffer.from(await fontResponse.arrayBuffer())

        await registerFontWithCanvas(family, weight, italic, fontBuffer as unknown)
      } catch (err) {
        console.warn(`Failed to load font file: ${family} ${weight} ${italic}`, err)
      }
    })

    await Promise.all(fontPromises)

    // Small delay to ensure fonts are registered
    await new Promise(resolve => {
      return setTimeout(resolve, 100)
    })
  } catch (error) {
    console.warn('Failed to load fonts on server:', error)
  }
}

/**
 * Client-side: Inject CSS and wait for fonts
 */
const loadFontsClient = async (url: string, fallbackUrl: string): Promise<boolean> => {
  let cssText = ''
  let usedFallback = false

  try {
    const res = await fetch(url)
    if (!res.ok) {
      throw new Error('Fallback CSS fetch failed')
    }
    cssText = await res.text()
  } catch (e) {
    // Fallback fetch
    try {
      const res = await fetch(fallbackUrl)
      if (!res.ok) {
        throw new Error('Fallback CSS fetch failed')
      }
      cssText = await res.text()
      usedFallback = true
    } catch (err) {
      console.warn('Failed to fetch font CSS on client', err)
      return false
    }
  }

  // Extract font URLs from CSS
  const fontUrls = extractFontUrls(cssText)

  const promises = fontUrls.map(async ({ url, family, weight, italic }) => {
    try {
      const fontFace = new FontFace(getRegisterFamilyName(family, weight, italic), `url(${url})`, {
        // это чинит Sofia Sans (болгарская кириллица):
        featureSettings: '"locl" 0',
        display: 'block',
      })

      await fontFace.load()
      document.fonts.add(fontFace)
    } catch (err) {
      console.warn(`Failed to load client font ${family}`, err)
    }
  })

  await Promise.all(promises)

  return usedFallback
}

/**
 * Extract font file URLs from Google Fonts CSS
 */
const extractFontUrls = (cssText: string): Array<{ url: string, family: string, weight: number, italic: boolean }> => {
  const fonts: Array<{ url: string, family: string, weight: number, italic: boolean }> = []

  // Regex to match @font-face rules
  const fontFaceRegex = /@font-face\s*{([^}]+)}/g
  let match

  while ((match = fontFaceRegex.exec(cssText)) !== null) {
    const block = match[1]

    // Extract properties
    const familyMatch = block.match(/font-family:\s*['"]([^'"]+)['"]/)
    const styleMatch = block.match(/font-style:\s*(\w+)/)
    const weightMatch = block.match(/font-weight:\s*(\d+)/)
    const urlMatch = block.match(/url\(([^)]+)\)/)

    if (familyMatch && urlMatch) {
      fonts.push({
        url: urlMatch[1].replace(/['"]/g, ''),
        family: familyMatch[1],
        weight: weightMatch ? parseInt(weightMatch[1]) : 400,
        italic: styleMatch ? styleMatch[1] === 'italic' : false,
      })
    }
  }

  return fonts
}

/**
 * Register font with node-canvas (for server-side rendering)
 * This is required for FabricJS v6 on Node.js
 */
const registerFontWithCanvas = async (
  family: string,
  weight: number,
  italic: boolean,
  fontBuffer: unknown,
): Promise<void> => {
  try {
    const os = await import('os')
    const path = await import('path')
    const fs = await import('fs')

    const registerFamilyName = getRegisterFamilyName(family, weight, italic)

    const fontPath = path.join(os.tmpdir(), `${registerFamilyName}.woff2`)

    // Write buffer to file
    fs.writeFileSync(fontPath, fontBuffer as string)

    // Register with node-canvas
    registerFont(fontPath, {
      family: registerFamilyName,
    })
  } catch (err) {
    console.warn('Failed to register font with node-canvas:', err)
  }
}

/**
 * Формирует уникальное и безопасное имя шрифта для переданных параметров (чтобы не путались разные жирности)
 * @param family Имя семейства
 * @param weight Жирность
 * @param italic Курсив
 * @returns Имя шрифта для регистрации
 */
export const getRegisterFamilyName = (family: string, weight: number, italic: boolean) => {
  return `${family.replace(/\s+/g, '-')}-${weight}-${italic ? 'italic' : 'normal'}`
}
